import { Box, Fade, Flex, Image, Spacer, Text } from '@chakra-ui/react';
import useSetArticleReaction from 'hooks/modules/articles/useSetArticleReaction';
import moment from 'moment';
import Carousel from 'presentation/components/shared/Carousel/Carousel';
import ReactionsRow from 'presentation/components/shared/ReactionsRow';
import TagsRow from 'presentation/components/shared/TagsRow';
import { FC, useState } from 'react';
import { getPhotoPreview, getPrivateChannelWidth } from 'utils/getPhotoPreview';
import { PhotoPreview } from 'enums/shared/photoPreview';
import FsLightbox from 'fslightbox-react';
import PollComponent from './PollComponent';
import useSetPollAnswer from 'hooks/modules/articles/useSetPollAnswer';
import HTMLReactParser from 'html-react-parser';
import { replaceAnchor } from 'presentation/utils/replaceAnchor';
import documentIcon from 'assets/img/icons/document.svg';
import downloadFile from 'presentation/utils/downloadFile';
import { getFileSizeString } from 'presentation/utils/getFileSize';
import { availableReactions } from 'rtkQuery/sharespro/mapper/shared/mapReactionListItemFromApi';
import { FaEye } from 'react-icons/fa';

interface Props {
  article: ArticleListItem;
  color: string;
  page: number;
}

const PrivateChannelDetailArticleCard: FC<Props> = ({ article, page }) => {
  const setReaction = useSetArticleReaction(
    article.id,
    article.selectedReactionId,
    page
  );
  const setPollAnswer = useSetPollAnswer(article.id, (article.poll?.id ?? -1), page);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    position: { x: 0, y: 0 },
  });
  const handleContextMenu = (event: {
    preventDefault: () => void;
    clientX: any;
    clientY: any;
  }): void => {
    event.preventDefault();
    setContextMenu({
      show: true,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const handleCloseContextMenu = (): void => {
    setContextMenu({ show: false, position: { x: 0, y: 0 } });
  };
  const gallery = article.image ? [article.image, ...article.gallery] : article.gallery;
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [toggler, setToggler] = useState(false);

  return (
    <Box onContextMenu={handleContextMenu}
         onClick={handleCloseContextMenu} ml="15px">
      <Text mb="15px" fontSize="32px" fontWeight={800}>
        {article.name}
      </Text>
      <Flex>
        <TagsRow
          tags={[
            ...(article.category ? [article.category] : []),
            moment(article.publicDate).format('DD.MM.yyyy'),
          ]}
          mb="15px"
        />
        <Spacer />
        <Box display="flex" alignItems="center">
          <FaEye color="grey" width="25px" height="17px" />
          <Text ml="5px" fontSize={20} color="grey">
            {article.viewCount}
          </Text>
        </Box>
      </Flex>
      {gallery.length !== 0 && (<><Carousel
        scrollBy={320}
        itemsCount={gallery.length}
        viewportItemsCount={2}
        showControls={gallery.length > 2}
        scrollType="fixed"
      >
        {gallery.length > 1 ? gallery.map((img, index) => (
          <Image
            src={gallery[index].url}
            h={gallery.length === 1 ? getPhotoPreview(gallery[0]) === PhotoPreview.Vertical ? '650px' : '300px' : '300px'}
            mb="20px"
            mr="2px"
            borderTopLeftRadius={index === 0 ? '16px' : '0'}
            key={index}
            borderBottomLeftRadius={index === 0 ? '16px' : '0'}
            borderTopEndRadius={gallery.length - 1 === index ? '16px' : '0'}
            borderBottomEndRadius={index === gallery.length - 1 ? '16px' : '0'}
            w={gallery.length === 1 ? getPhotoPreview(gallery[0]) === PhotoPreview.Vertical ? '450px' : getPrivateChannelWidth(getPhotoPreview(gallery[index])) : getPrivateChannelWidth(getPhotoPreview(gallery[index]))}
            objectFit={gallery.length === 1 ? 'contain' : 'cover'}
            onClick={() => {
              setToggler(!toggler);
              setSelectedIndex(index);
            }}
          />
        )) : (
          <Image
            h={getPhotoPreview(gallery[0]) === PhotoPreview.Vertical ? '900px' : getPhotoPreview(gallery[0]) === PhotoPreview.Horizontal ? '400px' : '650px'}
            w="full"
            blur="2xl"
            mb="20px"
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            sx={{ width: '100%', height: undefined, aspectRatio: gallery[0].width! / gallery[0].height! }}
            onClick={() => {
              setToggler(!toggler);
              setSelectedIndex(0);
            }}
            borderRadius="16px"
            src={gallery[0].url}
            objectFit="fill"
          />)}
      </Carousel>
        <FsLightbox
          toggler={toggler}
          sources={gallery.map((photo) => photo.url)}
          sourceIndex={selectedIndex}
          type="image"
          disableLocalStorage={false}
        /></>)
      } {article.video != null && (<Box
      h="full" w="full" mb="2px" blur="2xl"
      as="video"
      controls
      src={article.video.url}
      objectFit="contain"
    />)}
      <Text mb="16px" fontSize="20px">
        {HTMLReactParser(article.text ?? '', { replace: replaceAnchor })}
      </Text>
      {article.poll !== null && (<PollComponent poll={article.poll} selectedAnswer={(val) => {
        setPollAnswer(val);
      }} />)}
      <Box h="20px" />
      {article.presentation != null && (
        <Flex mb="16px" gap="8px">
          <Image src={documentIcon} w="37px" h="37px" onClick={() => downloadFile(article.presentation?.url ?? '')} />
          <Box alignItems="flex-end">
            <Text fontWeight="bold" fontSize={16}>{article.presentation.fileName}</Text>
            <Text fontWeight="normal" fontSize={12}>{getFileSizeString({
              bytes: article.presentation.size ?? 0,
              decimals: 2,
            })}</Text>
          </Box>
        </Flex>)}
      <ReactionsRow
        reactions={article.reactions}
        selectedReactionId={article.selectedReactionId}
        selectReaction={setReaction}
      />
      {contextMenu.show && (
        <Fade in={contextMenu.show}>
          <Box
            position="absolute"
            left={contextMenu.position.x - 20}
            top={contextMenu.position.y - 10}
            onScroll={handleCloseContextMenu}
            onScrollCapture={handleCloseContextMenu}
            onMouseLeave={handleCloseContextMenu}
          >
            <Flex
              flexWrap="wrap"
              maxW="300px"
              bg="white"
              borderRadius="10px"
              rounded="md"
              shadow="lg"
            >
              {Object.keys(availableReactions).map((key) => (
                <Box
                  key={key}
                  py="10px"
                  cursor="pointer"
                  px="10px"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReaction({
                      id: +key,
                      emoji: availableReactions[+key],
                    });
                    handleCloseContextMenu();
                  }}
                >
                  <Text fontSize="20px">{availableReactions[+key]}</Text>
                </Box>
              ))}
            </Flex>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default PrivateChannelDetailArticleCard;
